import queryString from 'query-string'
import { first, isEmpty } from 'lodash'

import { toast } from '@/components/toasts/toast.helpers'
import {
  getTagOptions,
  findPreviousDateRangeFromCurrentFilters,
  getAgentOptions,
} from '@/utils/helpers'
import { formatScorecardFilters, formatDisputes } from './helpers'
import { fetchingAPI, apiService } from '../../api'
import { setLoading, setData, setFilter, setTableHeaders, setTableRows } from './scorecards.redux'

export const fetchAgentsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading('agents', true))

  try {
    const { users } = await fetchingAPI(
      `${apiService.core}/api/organizations/${organizationId}/users`,
      'GET',
      dispatch
    )

    dispatch(setData('agents', getAgentOptions(users)))
    dispatch(setData('currentOrganizationId', organizationId))
  } catch (err) {
    toast.error('Failed to fetch agents', { cause: err })
  } finally {
    dispatch(setLoading('agents', false))
  }
}

export const fetchTagsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading({ tags: true }))

  try {
    const [tags, tagCategories] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'GET',
        dispatch
      ),
    ])

    const tagOptionsByCategory = []

    // Created grouped tag categories
    tagCategories.forEach((cat) => {
      tagOptionsByCategory.push({
        label: cat.name,
        options: getTagOptions(tags.filter((tag) => tag.tag_category_id === cat.id)),
      })
    })

    // Add uncategorized tags
    tagOptionsByCategory.push({
      label: 'Uncategorized',
      options: getTagOptions(tags.filter((tag) => !tag.tag_category_id)),
    })

    dispatch(setData('tags', tagOptionsByCategory))
  } catch (err) {
    toast.error('Failed to fetch tags', { cause: err })
  } finally {
    dispatch(setLoading({ tags: false }))
  }
}

export const fetchPlaybooksByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading('playbooks', true))

  try {
    const configProperties = queryString.stringify({ requested_properties: 'id,name,cid' })
    const playbooks = await fetchingAPI(
      `${apiService.core}/api/${organizationId}/configs?${configProperties}&active_only=True`,
      'GET',
      dispatch
    )
    const playbookOptions = playbooks
      .map((playbook) => ({
        value: playbook.cid,
        id: playbook.id,
        label: playbook.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(setData('playbooks', playbookOptions))
  } catch (err) {
    toast.error('Failed to fetch playbooks', { cause: err })
  } finally {
    dispatch(setLoading('playbooks', false))
  }
}

export const fetchPlaybookById = (playbookId) => async (dispatch) => {
  dispatch(setLoading('targetPlaybook', true))

  try {
    const playbook = await fetchingAPI(
      `${apiService.core}/api/configs/${playbookId}`,
      'GET',
      dispatch
    )

    dispatch(setData('targetPlaybook', playbook.config))
  } catch (err) {
    toast.error('Failed to fetch target playbook', { cause: err })
  } finally {
    dispatch(setLoading('targetPlaybook', false))
  }
}

export const fetchScorecardConfigById = (scorecardConfigId) => async (dispatch) => {
  dispatch(setLoading('targetScorecardConfig', true))

  try {
    const scorecardConfig = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardConfigId}`,
      'GET',
      dispatch
    )
    dispatch(setData('targetScorecardConfig', scorecardConfig))
  } catch (err) {
    toast.error('Failed to fetch target scorecard config', { cause: err })
  } finally {
    dispatch(setLoading('targetScorecardConfig', false))
  }
}

export const fetchAggregateScoresByConfigId =
  (scorecardId, returnFormat = null) =>
  async (dispatch, getState) => {
    dispatch(setLoading('scores', true))
    dispatch(setLoading('aggregateScores', true))
    dispatch(setLoading('aggregateFilters', true))
    const { scorecards } = getState()
    const filterString = formatScorecardFilters(scorecards.filters, null)

    try {
      let url = `${apiService.scorecard}/scoring/scores/byConfigId/${scorecardId}/aggregate?${filterString}`
      if (returnFormat) url = url.concat(`&return_format=${returnFormat}`)

      const scoreData = await fetchingAPI(url, 'GET', dispatch)
      dispatch(setData('allScores', scoreData.scorecard_ids || []))
      if (returnFormat === 'table') {
        dispatch(setTableHeaders('advancedExports', scoreData.headers || []))
        dispatch(setTableRows('advancedExports', scoreData.rows || []))
      } else {
        dispatch(setData('aggregateScores', scoreData.aggregate_scores || {}))
      }
      dispatch(setData('aggregateFilters', scoreData.filters || {}))
    } catch (err) {
      console.error(err)
      toast.error(err?.message || 'Failed to fetch scores')
    } finally {
      dispatch(setLoading('scores', false))
      dispatch(setLoading('aggregateScores', false))
      dispatch(setLoading('aggregateFilters', false))
    }
  }

// Streams the full scorecard export CSV and keeps it as a Blob.
//
// The response is handed straight to the download; it is deliberately NOT
// parsed. The only consumer of this data is the CSV download button, so
// parsing the server's CSV into row objects only to re-serialise it back into
// a CSV was pure overhead -- and it did not survive real exports. A 13-day
// export of org 7656 is ~60MB uncompressed; accumulating that into one JS
// string, parsing it into ~27k objects, putting those in Redux and then
// rebuilding the CSV (finally as a `data:` URI, which has its own size
// ceiling) killed the request with net::ERR_CONNECTION after 3.8 minutes.
// haproxy logged the server side as a clean 200, 28ms to first byte.
//
// A Blob is backed by disk rather than the JS heap, so the same export costs
// almost no memory. See CUS-154 / CUS-220.
// Object URLs pin their Blob in memory until revoked, and an export Blob can
// be hundreds of MB.
export const revokeStreamedCsvUrl = () => (dispatch, getState) => {
  const { advancedExportsCsvUrl } = getState().scorecards.data
  if (advancedExportsCsvUrl) {
    URL.revokeObjectURL(advancedExportsCsvUrl)
  }
  dispatch(setData('advancedExportsCsvUrl', ''))
}

// Report progress at most once per megabyte -- enough to look alive without
// dispatching on every chunk.
const CSV_PROGRESS_INTERVAL_BYTES = 1024 * 1024

export const fetchStreamingScores = (isCopilot) => async (dispatch, getState) => {
  const { scorecards } = getState()
  const filterString = formatScorecardFilters(scorecards.filters, null, isCopilot)
  const url = `${apiService.scorecard}/scoring/scores/scorecards/streaming/calls?${filterString}`

  dispatch(setLoading('advancedExportsStreamedCsv', true))
  dispatch(setData('advancedExportsCsvFailed', false))
  dispatch(setData('advancedExportsCsvBytes', 0))
  dispatch(revokeStreamedCsvUrl())

  try {
    // handleResponse resolves the raw ReadableStream for text/csv.
    const csvStream = await fetchingAPI(url, 'POST', dispatch)
    const reader = csvStream.getReader()

    // Chunks are kept as the Uint8Arrays they arrive as and handed to a Blob.
    // The old code decoded each chunk and concatenated it onto a string, which
    // for a 60MB export meant ~120MB of UTF-16 before parsing even started.
    const chunks = []
    let bytesReceived = 0
    let bytesReported = 0

    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { value, done } = await reader.read()
      if (done) break

      chunks.push(value)
      bytesReceived += value.byteLength

      if (bytesReceived - bytesReported >= CSV_PROGRESS_INTERVAL_BYTES) {
        bytesReported = bytesReceived
        dispatch(setData('advancedExportsCsvBytes', bytesReceived))
      }
    }

    dispatch(setData('advancedExportsCsvBytes', bytesReceived))
    const blob = new Blob(chunks, { type: 'text/csv;charset=utf-8' })
    dispatch(setData('advancedExportsCsvUrl', URL.createObjectURL(blob)))
  } catch (err) {
    console.error(err)
    toast.error(err?.message || 'Failed to fetch scores')
    // Recorded so the download button reports the failure instead of quietly
    // handing back the 20-row preview, which is what made every backend
    // failure look like a hard 20-row cap.
    dispatch(setData('advancedExportsCsvFailed', true))
  } finally {
    dispatch(setLoading('advancedExportsStreamedCsv', false))
  }
}

export const fetchScorecardByScorecardConfigs =
  (isPreview = false, copilot) =>
  async (dispatch, getState) => {
    dispatch(setLoading('scores', true))
    dispatch(setLoading('aggregateScores', true))
    dispatch(setLoading('aggregateFilters', true))
    const { scorecards } = getState()
    const filterString = formatScorecardFilters(scorecards.filters, null, copilot, isPreview)

    try {
      const url = `${apiService.scorecard}/scoring/scores/scorecards/calls?${filterString}`
      const scoreData = await fetchingAPI(url, 'GET', dispatch)
      dispatch(setTableHeaders('advancedExports', scoreData.headers || []))
      dispatch(setTableRows('advancedExports', scoreData.rows || []))
      dispatch(setData('aggregateFilters', scoreData.filters || {}))
    } catch (err) {
      console.error(err)
      toast.error(err?.message || 'Failed to fetch scores')
    } finally {
      dispatch(setLoading('scores', false))
      dispatch(setLoading('aggregateScores', false))
      dispatch(setLoading('aggregateFilters', false))
    }
  }

export const fetchAggregateScoresByMultipleScorecards =
  (returnFormat = null) =>
  async (dispatch, getState) => {
    dispatch(setLoading('scores', true))
    dispatch(setLoading('aggregateScores', true))
    dispatch(setLoading('aggregateFilters', true))
    const { scorecards } = getState()
    const filterString = formatScorecardFilters(scorecards.filters)

    try {
      let url = `${apiService.scorecard}/scoring/scores/aggregate/multiple?${filterString}`
      if (returnFormat) url = url.concat(`&return_format=${returnFormat}`)

      const scoreData = await fetchingAPI(url, 'GET', dispatch)
      dispatch(setData('allScores', scoreData.scorecard_ids || []))
      if (returnFormat === 'table') {
        dispatch(setTableHeaders('advancedExports', scoreData.headers || []))
        dispatch(setTableRows('advancedExports', scoreData.rows || []))
      } else {
        dispatch(setData('aggregateScores', scoreData.aggregate_scores || {}))
      }
      dispatch(setData('aggregateFilters', scoreData.filters || {}))
    } catch (err) {
      console.error(err)
      toast.error(err?.message || 'Failed to fetch scores')
    } finally {
      dispatch(setLoading('scores', false))
      dispatch(setLoading('aggregateScores', false))
      dispatch(setLoading('aggregateFilters', false))
    }
  }

export const fetchAggregateScoresByUserByConfigId = (scorecardId) => async (dispatch, getState) => {
  dispatch(setLoading('aggregateScoresByUser', true))
  dispatch(setLoading('aggregateFilters', true))
  const { scorecards } = getState()
  const filterString = formatScorecardFilters(scorecards.filters)

  try {
    const scoreData = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/byConfigId/${scorecardId}/aggregate?group_by=agents&return_format=table&metrics=sections&${filterString}`,
      'GET',
      dispatch
    )
    dispatch(setTableRows('scorecardDashboard', scoreData.rows || []))
    dispatch(setTableHeaders('scorecardDashboard', scoreData.headers || []))
    dispatch(setData('callsCount', scoreData.call_count || ''))
    dispatch(setData('aggregateFilters', scoreData.filters || {}))
    dispatch(fetchScorecardConfigById(scorecardId))
  } catch (err) {
    toast.error('Failed to fetch scores', { cause: err })
  } finally {
    dispatch(setLoading('aggregateScoresByUser', false))
    dispatch(setLoading('aggregateFilters', false))
  }
}

export const fetchAggregateScores = (returnFormat) => async (dispatch, getState) => {
  dispatch(setLoading('scores', true))
  dispatch(setLoading('aggregateScores', true))
  dispatch(setLoading('aggregateFilters', true))
  const { scorecards } = getState()
  const filterString = formatScorecardFilters(scorecards.filters, null)
  try {
    if (!isEmpty(scorecards.filters.scorecards)) {
      const firstScorecard = first(scorecards.filters.scorecards)
      let url = `${apiService.scorecard}/scoring/scores/byConfigId/${firstScorecard.id}/aggregate?${filterString}`
      if (returnFormat) url = url.concat(`&return_format=${returnFormat}`)

      const scoreData = await fetchingAPI(url, 'GET', dispatch)
      dispatch(setData('allScores', scoreData.scorecard_ids || []))
      dispatch(setData('scores', scoreData.scorecard_ids || []))
      if (returnFormat === 'table') {
        dispatch(setTableHeaders('advancedExports', scoreData.headers || []))
        dispatch(setTableRows('advancedExports', scoreData.rows || []))
      } else {
        dispatch(setData('aggregateScores', scoreData.aggregate_scores || {}))
      }
      dispatch(setData('aggregateFilters', scoreData.filters || {}))
      if (firstScorecard && firstScorecard.id && firstScorecard.name) {
        dispatch(fetchScorecardConfigById(firstScorecard.id))
        dispatch(
          setFilter('scorecards', [
            {
              value: firstScorecard.id,
              label: firstScorecard.name,
              sid: firstScorecard.sid,
            },
          ])
        )
      }
    } else {
      dispatch(setData('allScores', []))
      dispatch(setData('scores', []))
      dispatch(setData('aggregateScores', {}))
      dispatch(setData('aggregateFilters', {}))
      dispatch(setFilter('scorecards', []))
    }
  } catch (err) {
    console.error(err)
    toast.error(err?.message || 'Failed to fetch scores')
  } finally {
    dispatch(setLoading('scores', false))
    dispatch(setLoading('aggregateScores', false))
    dispatch(setLoading('aggregateFilters', false))
  }
}

export const fetchAggregateScoresByOrgIdMultipleScorecards =
  (organizationId) => async (dispatch, getState) => {
    dispatch(setLoading('reporting', true))
    dispatch(setLoading('reportingComparison', true))
    const { scorecards } = getState()
    const { startDate, endDate } = scorecards.filters
    const [newStartDate, newEndDate] = findPreviousDateRangeFromCurrentFilters(startDate, endDate)
    const filterString = formatScorecardFilters(scorecards.filters)
    const previousFilterString = formatScorecardFilters({
      ...scorecards.filters,
      startDate: newStartDate,
      endDate: newEndDate,
    })

    try {
      const scoreData = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/aggregate/multiple?requested_organization_id=${organizationId}&${filterString}`,
        'GET',
        dispatch
      )
      dispatch(setData('reporting', scoreData))
      dispatch(setData('aggregateFilters', scoreData.filters || {}))

      const previousRangeScoreData = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/aggregate/multiple?requested_organization_id=${organizationId}&${previousFilterString}`,
        'GET',
        dispatch
      )
      dispatch(setData('reportingComparison', previousRangeScoreData))
    } catch (err) {
      dispatch(setFilter('reporting', {}))
      toast.error('Failed to fetch scores', { cause: err })
    } finally {
      dispatch(setLoading('reporting', false))
      dispatch(setLoading('reportingComparison', false))
    }
  }

export const fetchAggregateScoresByOrgIdMultipleScorecardsByDay =
  (organizationId) => async (dispatch, getState) => {
    dispatch(setLoading('reportingByDay', true))
    const { scorecards } = getState()
    const filterString = formatScorecardFilters(scorecards.filters)

    try {
      const scoreData = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/aggregate/multiple/by_date?requested_organization_id=${organizationId}&${filterString}`,
        'GET',
        dispatch
      )
      dispatch(setData('reportingByDay', scoreData))
    } catch (err) {
      dispatch(setFilter('reportingByDay', {}))
      toast.error('Failed to fetch scores', { cause: err })
    } finally {
      dispatch(setLoading('reportingByDay', false))
    }
  }

export const fetchAggregateScoresByOrgIdMultipleScorecardsByUser =
  (organizationId) => async (dispatch, getState) => {
    dispatch(setLoading('reportingByUser', true))
    const { scorecards } = getState()

    const filterString = formatScorecardFilters(scorecards.filters)

    try {
      const scoreData = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/aggregate/multiple/users?requested_organization_id=${organizationId}&${filterString}`,
        'GET',
        dispatch
      )
      dispatch(setData('reportingByUser', scoreData))
    } catch (err) {
      dispatch(setFilter('reportingByUser', {}))
      toast.error('Failed to fetch scores', { cause: err })
    } finally {
      dispatch(setLoading('reportingByUser', false))
    }
  }

export const fetchAggregateScoresByUser = () => async (dispatch, getState) => {
  dispatch(setLoading('aggregateScoresByUser', true))
  dispatch(setLoading('aggregateFilters', true))
  const { scorecards } = getState()
  const filterString = formatScorecardFilters(scorecards.filters)
  try {
    if (!isEmpty(scorecards.filters.scorecards)) {
      const firstScorecard = first(scorecards.filters.scorecards)
      const scoreData = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/byConfigId/${firstScorecard.id}/aggregate?group_by=agents&${filterString}`,
        'GET',
        dispatch
      )
      dispatch(setData('aggregateScoresByUser', scoreData.aggregate_scores || []))
      dispatch(setData('aggregateFilters', scoreData.filters || {}))
    }
  } catch (err) {
    toast.error('Failed to fetch scores', { cause: err })
  } finally {
    dispatch(setLoading('aggregateScoresByUser', false))
    dispatch(setLoading('aggregateFilters', false))
  }
}

export const fetchScoresByScorecardId = () => async (dispatch, getState) => {
  dispatch(setLoading('scoresTable', true))
  const { scorecards } = getState()
  const filterString = formatScorecardFilters(scorecards.filters, null)
  try {
    const scorecardId = first(scorecards.filters.scorecards).value
    const scorecardConfigs = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/byConfigId/${scorecardId}?${filterString}`,
      'GET',
      dispatch
    )
    dispatch(setData('filteredScores', scorecardConfigs))
  } catch {
    toast.error('Failed to fetch all scorecards')
  } finally {
    dispatch(setLoading('scoresTable', false))
  }
}

export const fetchScoreById = (scorecardId) => async (dispatch) => {
  dispatch(setLoading('currentScore', true))

  try {
    const scorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/${scorecardId}`,
      'GET',
      dispatch
    )
    dispatch(setData('currentScore', scorecard))
  } catch (err) {
    toast.error('Failed to fetch scores', { cause: err })
  } finally {
    dispatch(setLoading('currentScore', false))
  }
}

export const fetchScorecardsByCallId =
  (callId, organizationId, callType = 'call') =>
  async (dispatch) => {
    if (!callId || !organizationId) return
    dispatch(setLoading('scorecards', true))

    try {
      const scorecards = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/by_call_id/${callId}?requested_organization_id=${organizationId}&call_type=${callType}`,
        'GET',
        dispatch
      )
      dispatch(setData('scorecards', scorecards))
    } catch (err) {
      toast.error('Failed to fetch scorecards', { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const fetchScorecardsByOrg =
  (organizationId, scorecardType = null) =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))
    let url = `${apiService.scorecard}/scoring/scorecards/current?requested_organization_id=${organizationId}`
    if (scorecardType) {
      url += `&scorecard_type=${scorecardType}`
    }
    try {
      const scorecardConfigs = await fetchingAPI(url, 'GET', dispatch)
      const sortedScorecardConfigs = scorecardConfigs.sort((a, b) => a.name?.localeCompare(b.name))
      dispatch(setData('scorecardConfigs', sortedScorecardConfigs))
      dispatch(setData('currentOrganizationId', organizationId))
    } catch (err) {
      toast.error(`Failed to fetch scorecards for organization: ${organizationId}`, { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const fetchScorecardOptionsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading('scorecardOptions', true))

  try {
    const scorecardConfigs = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/current?requested_organization_id=${organizationId}`,
      'GET',
      dispatch
    )

    const scorecardOptions = scorecardConfigs
      .map((scorecard) => ({
        value: scorecard.id,
        label: scorecard.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    dispatch(setData('scorecardOptions', scorecardOptions))
    dispatch(setData('currentOrganizationId', organizationId))
  } catch (err) {
    toast.error(`Failed to fetch scorecards for organization: ${organizationId}`, { cause: err })
  } finally {
    dispatch(setLoading('scorecardOptions', false))
  }
}

export const fetchNotableCalls = (section) => async (dispatch, getState) => {
  const scoreData = {}
  scoreData.percentage_score = section.percentage_score
  dispatch(setLoading('notableCalls', true))
  const { scorecards } = getState()
  const filterString = formatScorecardFilters(scorecards.filters, scoreData)
  try {
    const scorecardId = first(scorecards.filters.scorecards).value
    const notableCallsFromApi = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/byConfigId/${scorecardId}/notableCalls/${section.section_id}?${filterString}`,
      'GET',
      dispatch
    )
    dispatch(
      setData(`aboveThreshold`, {
        ...scorecards.data?.aboveThreshold,
        [section.section_id]: notableCallsFromApi.above_threshold,
      })
    )
    dispatch(
      setData(`belowThreshold`, {
        ...scorecards.data?.belowThreshold,
        [section.section_id]: notableCallsFromApi.below_threshold,
      })
    )
  } catch (err) {
    toast.error('Failed to fetch notable calls for organization', { cause: err })
  } finally {
    dispatch(setLoading('notableCalls', false))
  }
}

export const fetchTargetScorecardData = (scorecardConfigId) => async (dispatch, getState) => {
  dispatch(setLoading('targetScorecardConfig', true))
  dispatch(setLoading('targetPlaybook', true))

  try {
    await dispatch(fetchScorecardConfigById(scorecardConfigId))
    const { scorecards } = getState()
    const targetPlaybookId = scorecards.data.targetScorecardConfig.config_id
    await dispatch(fetchPlaybookById(targetPlaybookId))
  } catch (err) {
    toast.error('Failed to fetch target scorecard data', { cause: err })
  } finally {
    dispatch(setLoading('targetScorecardConfig', false))
    dispatch(setLoading('targetPlaybook', false))
  }
}

export const addScorecard = (scorecard, organizationId) => async (dispatch) => {
  dispatch(setLoading('scorecards', true))

  try {
    const body = JSON.stringify({
      ...scorecard,
      min_duration_in_seconds: scorecard.min_duration_in_minutes * 60 || 60,
    })

    const newScorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard`,
      'POST',
      dispatch,
      body
    )

    await dispatch(fetchScorecardsByOrg(organizationId))
    await dispatch(setData('targetScorecardConfig', newScorecard))
    return newScorecard
  } catch (err) {
    toast.error('Failed to add scorecard', { cause: err })
    return null
  } finally {
    dispatch(setLoading('scorecards', false))
  }
}

export const newCustomSection = (configDraft) => async (dispatch) => {
  dispatch(setLoading('scorecards', true))
  try {
    const body = JSON.stringify({
      ...configDraft,
    })

    const newScorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${body.id}`,
      'PUT',
      dispatch,
      body
    )
    dispatch(setData('targetScorecardConfig', newScorecard))
    return {
      newScorecard,
    }
  } catch (err) {
    toast.error('Failed to create measure', { cause: err })
    return null
  } finally {
    dispatch(setLoading('scorecards', false))
  }
}

export const newCustomMeasure = (configDraft) => async (dispatch, getState) => {
  dispatch(setLoading('scorecards', true))
  const {
    scorecards: { data },
  } = getState()
  const { ssid } = data?.targetScorecardSection
  try {
    const body = JSON.stringify({
      ...configDraft,
    })
    const newScorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${configDraft.id}`,
      'PUT',
      dispatch,
      body
    )
    const newSection = newScorecard.sections.find(
      (scorecardSection) => scorecardSection.ssid === ssid
    )
    dispatch(setData('targetScorecardConfig', newScorecard))
    dispatch(setData('targetScorecardSection', newSection))
    return {
      newScorecard,
      newSection,
    }
  } catch (err) {
    toast.error('Failed to create measure', { cause: err })
    return null
  } finally {
    dispatch(setLoading('scorecards', false))
  }
}

export const copyScorecard =
  (scorecardConfig, existingCopies = 0, scorecardType = 'automated', newOrganizationId) =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))
    const copyPrompt = async (promptId) => {
      const response = await fetchingAPI(
        `${apiService.summary}/prompts/${promptId}/copy?copy_to_organization_id=${newOrganizationId}`,
        'POST',
        dispatch
      )
      return {
        oldId: promptId,
        newId: response?.id,
      }
    }

    try {
      const newName =
        existingCopies > 0
          ? `COPY OF ${scorecardConfig.name} (${existingCopies + 1})`
          : `COPY OF ${scorecardConfig.name}`

      let copiedScorecardConfig = {
        ...scorecardConfig,
        name: newName,
        active: false,
        organization_id: newOrganizationId,
        users: [],
        users_inclusive: true,
        tags: [],
        tags_inclusive: true,
        config_cids: [],
      }

      delete copiedScorecardConfig.sid
      delete copiedScorecardConfig.uuid
      delete copiedScorecardConfig.id
      delete copiedScorecardConfig.created_by
      delete copiedScorecardConfig.created_on

      copiedScorecardConfig.sections.forEach((section) => {
        delete section.uuid
        delete section.ssid
        delete section.id
        section.measures.forEach((measure) => {
          delete measure.smid
          delete measure.uuid
          delete measure.id
          measure.criteria.forEach((criteria) => {
            delete criteria.scid
            delete criteria.uuid
            delete criteria.id
          })
        })
      })

      // find all prompts and then create copies of prompts
      const promptsToCopy = [
        copiedScorecardConfig.eligibility_prompt_id,
        ...copiedScorecardConfig.sections.flatMap((section) =>
          section.measures.flatMap((measure) =>
            measure.criteria.flatMap((criteria) => criteria.prompt_id)
          )
        ),
      ].filter((promptId) => promptId !== null)

      const scorecardConfigWithPrompts = await Promise.all(
        promptsToCopy.map((promptId) => copyPrompt(promptId))
      ).then((copiedIds) => {
        return {
          ...copiedScorecardConfig,
          eligibility_prompt_id:
            copiedIds.find(
              (promptId) => promptId.oldId === copiedScorecardConfig.eligibility_prompt_id
            )?.newId || null,
          sections: copiedScorecardConfig.sections.map((section) => ({
            ...section,
            measures: section.measures.map((measure) => ({
              ...measure,
              criteria: measure.criteria.map((criteria) => ({
                ...criteria,
                prompt_id:
                  copiedIds.find((promptId) => promptId.oldId === criteria.prompt_id)?.newId ||
                  null,
              })),
            })),
          })),
        }
      })

      copiedScorecardConfig = JSON.stringify(scorecardConfigWithPrompts)

      const newScorecard = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard`,
        'POST',
        dispatch,
        copiedScorecardConfig
      )

      await dispatch(fetchScorecardsByOrg(newOrganizationId, scorecardType))
      await dispatch(setData('targetScorecardConfig', newScorecard))
    } catch (err) {
      toast.error('Failed to copy scorecard', { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const editScorecard = (scorecard, section, organizationId) => async (dispatch) => {
  dispatch(setLoading('scorecards', true))

  try {
    const body = JSON.stringify({
      ...scorecard,
    })
    const newScorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecard.id}`,
      'PUT',
      dispatch,
      body
    )
    const newSection = newScorecard.sections.find(
      (scorecardSection) => scorecardSection.name === section.name
    )

    await dispatch(fetchScorecardsByOrg(organizationId))
    await dispatch(setData('targetScorecardConfig', newScorecard))
    await dispatch(setData('targetScorecardSection', newSection))
    return {
      newScorecard,
      newSection,
    }
  } catch (err) {
    if (err?.status === 409) {
      toast.error(
        'Another user saved a new version while you were editing. Please note your changes, navigate to the newest version, and try again.',
        { cause: err }
      )
    } else {
      toast.error('Failed to edit scorecard', { cause: err })
    }
    return null
  } finally {
    dispatch(setLoading('scorecards', false))
  }
}

export const editSectionName =
  (scorecardSid, sectionSsid, name, organizationId) => async (dispatch) => {
    dispatch(setLoading('scorecards', true))
    const nameFilter = queryString.stringify({ section_name: name })
    try {
      const newScorecard = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardSid}/section/${sectionSsid}?${nameFilter}`,
        'PATCH',
        dispatch
      )

      const newSection = newScorecard.sections.find(
        (scorecardSection) => scorecardSection.ssid === sectionSsid
      )

      await dispatch(fetchScorecardsByOrg(organizationId))
      await dispatch(setData('targetScorecardConfig', newScorecard))
      await dispatch(setData('targetScorecardSection', newSection))
      return newScorecard
    } catch (err) {
      toast.error('Failed to edit scorecard section name', { cause: err })
      return null
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const editMeasureName =
  (scorecardSid, sectionSsid, measureSmid, name, organizationId) => async (dispatch) => {
    dispatch(setLoading('scorecards', true))
    const nameString = queryString.stringify({ measure_name: name })
    try {
      const newScorecard = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardSid}/section/${sectionSsid}/measure/${measureSmid}?${nameString}`,
        'PATCH',
        dispatch
      )

      const newSection = newScorecard.sections.find(
        (scorecardSection) => scorecardSection.ssid === sectionSsid
      )

      await dispatch(fetchScorecardsByOrg(organizationId))
      await dispatch(setData('targetScorecardConfig', newScorecard))
      await dispatch(setData('targetScorecardSection', newSection))
      return newScorecard
    } catch (err) {
      toast.error('Failed to edit scorecard measure name', { cause: err })
      return null
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const editScorecardSettings =
  ({ organizationId, ...params }) =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))
    const { min_duration_in_minutes, ...rest } = params
    const formattedParams = rest
    if (min_duration_in_minutes) {
      formattedParams.min_duration_in_seconds = min_duration_in_minutes * 60
    }
    try {
      const updatedScorecard = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${params.sid}/settings`,
        'PATCH',
        dispatch,
        JSON.stringify(formattedParams)
      )
      await dispatch(setData('targetScorecardConfig', updatedScorecard))
      toast.success('Your scorecard has been updated')
      await dispatch(fetchScorecardsByOrg(organizationId))
    } catch (err) {
      toast.error('Failed to update scorecard settings', { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const getMissingCriteria = (config_id, scorecard_config_id) => async (dispatch) => {
  dispatch(setLoading('missingCriteria', true))
  try {
    const missingCriteria = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/missing_criteria?config_id=${config_id}&scorecard_config_id=${scorecard_config_id}`,
      'GET',
      dispatch
    )
    await dispatch(setData('targetScorecardMissingCriteria', missingCriteria))
  } catch (err) {
    toast.error('Failed to get missing criteria', { cause: err })
  } finally {
    dispatch(setLoading('missingCriteria', false))
  }
}

export const removeMissingCriteria = () => async (dispatch) => {
  await dispatch(setData('targetScorecardMissingCriteria', []))
}

export const updatePlaybookVersionForScorecard = (scorecard) => async (dispatch) => {
  dispatch(setLoading('scorecards', true))

  try {
    const updatedScorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/update_playbook_version/${scorecard.id}?requested_organization_id=${scorecard.organization_id}`,
      'GET',
      dispatch
    )
    await dispatch(setData('targetScorecardConfig', updatedScorecard))
    await dispatch(fetchScorecardsByOrg(scorecard.organization_id))
    return updatedScorecard
  } catch (err) {
    toast.error('Failed to update playbook version', { cause: err })
    return null
  } finally {
    dispatch(setLoading('scorecards', false))
  }
}

export const createScorecardVersion = (scorecard, organizationId) => async (dispatch) => {
  dispatch(setLoading('scorecards', true))
  try {
    const body = JSON.stringify({ ...scorecard })

    const updatedScorecard = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecard.id}`,
      'PUT',
      dispatch,
      body
    )
    await dispatch(setData('targetScorecardConfig', updatedScorecard))
    await dispatch(fetchScorecardsByOrg(organizationId))
    return updatedScorecard
  } catch (err) {
    toast.error('Failed to create new scorecard config version', { cause: err })
    return null
  } finally {
    dispatch(setLoading('scorecards', false))
  }
}

export const toggleScorecardActive =
  (scorecard, scorecardType = null) =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))

    try {
      const updatedScorecard = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecard.sid}/settings?toggle_active=True`,
        'PATCH',
        dispatch
      )
      await dispatch(setData('targetScorecardConfig', updatedScorecard))
      await dispatch(fetchScorecardsByOrg(scorecard.organization_id, scorecardType))
    } catch (err) {
      toast.error('Failed to update scorecard settings', { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const updateScorecardSettings =
  (scorecard, settings, scorecardType = 'automated') =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))

    try {
      const updatedScorecard = await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecard.sid}/settings`,
        'PATCH',
        dispatch,
        JSON.stringify(settings)
      )
      dispatch(setData('targetScorecardConfig', updatedScorecard))
      await dispatch(fetchScorecardsByOrg(scorecard.organization_id, scorecardType))
    } catch (err) {
      toast.error('Failed to update scorecard settings', { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const deleteScorecard =
  (scorecardId, organizationId, scorecardType = 'automated') =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))

    try {
      await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/scorecard/${scorecardId}`,
        'DELETE',
        dispatch
      )
      await dispatch(fetchScorecardsByOrg(organizationId, scorecardType))
    } catch (err) {
      toast.error('Failed to delete scorecard', { cause: err })
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }
export const unscoreScorecard =
  (scorecardId, reason = '') =>
  async (dispatch) => {
    dispatch(setLoading('scorecards', true))

    try {
      const reasonParam = reason ? `?reason=${encodeURIComponent(reason)}` : ''

      const response = await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/scorecards/${scorecardId}${reasonParam}`,
        'DELETE',
        dispatch
      )
      toast.success('Scorecard successfully removed. This action has been logged.')
      return response
    } catch (err) {
      if (err.message === 'User not authorized to perform that action.') {
        return Promise.reject(
          new Error(
            `You have reached your monthly limit of 100 unscore actions. Please get in touch with support for further assistance.`
          )
        )
      }
      return Promise.reject(err)
    } finally {
      dispatch(setLoading('scorecards', false))
    }
  }

export const selectScorecard = (scorecard) => (dispatch) => {
  dispatch(setData('selectedScorecards', [scorecard]))
}

export const removeSelectedScorecard = () => (dispatch) => {
  dispatch(setData('selectedScorecards', []))
}

export const resetScorecardConfigs = () => (dispatch) => {
  dispatch(setData('scorecardConfigs', []))
}

export const resetSelectedScorecardConfig = () => (dispatch) => {
  dispatch(setData('targetScorecardConfig', {}))
}

export const setCurrentOrganizationId = (id) => (dispatch) => {
  dispatch(setData('currentOrganizationId', id))
}

export const getAllDisputes = () => async (dispatch, getState) => {
  dispatch(setLoading('disputes', true))
  try {
    const { currentUser } = getState()
    const { organizationid: orgId } = currentUser
    const disputes = await fetchingAPI(
      `${apiService.scorecard}/disputes/all?requested_organization_id=${orgId}`,
      'GET',
      dispatch
    )
    const formattedDisputes = formatDisputes(disputes)
    dispatch(setData('disputes', formattedDisputes))
  } catch (err) {
    toast.error('Failed to get disputes', { cause: err })
  } finally {
    dispatch(setLoading('disputes', false))
  }
}

export const fetchDispositionsByOrg = (organizationId) => async (dispatch) => {
  dispatch(setLoading('dispositions', true))

  try {
    const dispositions = await fetchingAPI(
      `${apiService.reporting}/api/dispositions?organization_id=${organizationId}`,
      'GET',
      dispatch
    )
    const dispoOptions = dispositions
      .map(({ name }) => ({
        value: name,
        label: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    dispatch(setData('dispositions', dispoOptions))
  } catch (err) {
    toast.error('Failed to fetch dispositions', { cause: err })
  } finally {
    dispatch(setLoading('dispositions', false))
  }
}
